import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { Footer } from 'src/components/moleculesLegacy/Footer';
import { SubmissionType } from 'src/components/organismsLegacy';
import CustomHeader from 'src/components/public/CustomHeader';
import ProgressBar from 'src/components/public/ProgressBar';
import ResponsiveContainer from 'src/components/public/ResponsiveContainer';
import { selectSubmissionType } from 'src/creators/OpenReporting.creators';
import { CategoryDisplayType, useGetPublicCategoriesQuery } from 'src/graphql';
import { State } from 'src/types';
import {
  Container,
  CustomHeaderContainer,
  SubmissionTypesList,
} from './SubmissionTypes.styles';

interface Props {
  isCountryEnabled?: boolean;
  isSiteEnabled?: boolean;
}

export const SubmissionTypes = ({
  isCountryEnabled = false,
  isSiteEnabled = false,
}: Props) => {
  const { t } = useTranslation();
  const organisation = useSelector(
    (state: State) => state.openReporting?.openReportReducer?.organisation,
  );
  const reportedCategory = useSelector(
    (state: State) => state.openReporting?.openReportReducer?.reportedCategory,
  );
  const [selected, setSelected] = useState<CategoryDisplayType | null>(null);
  const { data: publicCategories } = useGetPublicCategoriesQuery({
    slug: organisation ?? '',
  });
  const dispatch = useDispatch();
  useEffect(() => {
    const selectedType =
      publicCategories?.getPublicCategories?.find(
        (type: CategoryDisplayType) => type.id === reportedCategory,
      ) || null;

    setSelected(selectedType);
  }, [publicCategories?.getPublicCategories, reportedCategory]);

  const showQuestionFlow = selected?.question;
  const nextLink = showQuestionFlow ? 'question' : 'date';

  return (
    <Container>
      <CustomHeaderContainer>
        <CustomHeader organisation={organisation} />
      </CustomHeaderContainer>

      <ResponsiveContainer
        includeLeftCircleButton={true}
        includeRightCircleButton={true}
        includeBottomButton={true}
        backLinkTo={`/${organisation}/open-reporting/${
          isSiteEnabled ? 'site' : isCountryEnabled ? 'country' : 'relationship'
        }`}
        nextLinkTo={`/${organisation}/open-reporting/${nextLink}`}
        nextLinkDisabled={!selected}
        buttonText={t('Buttons.create')}
        buttonStyles={{ textTransform: 'uppercase' }}
        nextButtonOnClick={() => {
          if (selected) {
            dispatch(selectSubmissionType(selected));
          }
        }}
      >
        <div className="public-reporting-header">
          <h2 className="public-reporting-heading">
            {t('public.SubmissionTypes.reportIssue')}
          </h2>
          <ProgressBar percentage={20} />
          <h3 className="public-reporting-subheading">
            {t('public.SubmissionTypes.whatType')}
          </h3>
        </div>

        <SubmissionTypesList>
          {publicCategories?.getPublicCategories?.map((type, index) => (
            <SubmissionType
              active={
                selected !== null &&
                (selected.id === undefined
                  ? selected.name === type.name
                  : selected.id === type.id)
              }
              key={index.toString()}
              index={index}
              onClick={() => setSelected(type)}
              name={type.name}
              description={type.body}
            />
          ))}
        </SubmissionTypesList>
      </ResponsiveContainer>

      <Footer id="desktop-powered-by-vault" />
    </Container>
  );
};
